import * as R from 'ramda';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
import {
  DEFAULT_TIME_RATE_UNIT_OPTIONS,
  DEFAULT_VOLUME_RATE_UNIT_OPTIONS,
  DEFAULT_WEIGHT_RATE_UNIT_OPTIONS,
  DEFAULT_QUANTITY_RATE_UNIT_OPTIONS,
} from '../../helpers/options';
//////////////////////////////////////////////////

const validateMinesOrFloat = (value) => {
  if (R.equals('-', value)) {
    return false;
  }
  return R.not(R.test(GC.FLOAT_REGEXP, Number(value).toFixed(4)));
};

export const isInvalidChargeRateField = ({ name, value }) => R.and(
  R.test(/\.rate$/, name),
  validateMinesOrFloat(value),
);

// accessorials
export const getFirstAccessorial = (filter, accessorials) => R.compose(
  R.when(R.is(Object), R.identity()),
  R.head(),
  R.filter(filter),
)(accessorials);

const fuelAccessorialFilter = (item) => G.isTrue(
  R.prop(GC.FIELD_ACCESSORIAL_FUEL_RELATED, item),
);

const fuelAccessorialFilterForDO = (item) => R.and(
  G.isTrue(R.prop(GC.FIELD_ACCESSORIAL_FUEL_RELATED, item)),
  R.or(
    G.isFalse(R.prop(GC.FIELD_CHARGE_INTERNAL_EXPENSE, item)),
    G.isNilOrEmpty(R.prop(GC.FIELD_CHARGE_INTERNAL_EXPENSE, item)),
  ),
);

const noFuelAccessorialFilterForDO = (item) => {
  const { advancePayment, internalExpense } = item;

  if (G.isTrue(advancePayment)) return false;

  if (G.isTrue(internalExpense)) return false;

  return G.isFalse(R.prop(GC.FIELD_ACCESSORIAL_FUEL_RELATED, item));
};

export const noFuelAccessorialFilter = (item) => G.isFalse(
  R.prop(GC.FIELD_ACCESSORIAL_FUEL_RELATED, item),
);

export const getConfiguredAccessorialLabel = (item) => {
  const { rate, currency, rateType, rateUnit, displayedValue } = item;

  if (R.includes(rateType, [
    GC.CHARGE_RATE_TYPE_LINE_HAUL,
    GC.CHARGE_RATE_TYPE_TAXABLE_TOTAL,
    GC.CHARGE_RATE_TYPE_CUSTOMER_RATE,
    GC.CHARGE_RATE_TYPE_CUSTOMER_LINE_HAUL,
  ])) return `${displayedValue} - ${rate}%`;

  const label = `${displayedValue} - ${G.getCurrencySymbol(currency)}${R.defaultTo('', rate)}`;

  const unitOptions = {
    [GC.CHARGE_RATE_TYPE_TIME]: DEFAULT_TIME_RATE_UNIT_OPTIONS,
    [GC.CHARGE_RATE_TYPE_VOLUME]: DEFAULT_VOLUME_RATE_UNIT_OPTIONS,
    [GC.CHARGE_RATE_TYPE_WEIGHT]: DEFAULT_WEIGHT_RATE_UNIT_OPTIONS,
    [GC.CHARGE_RATE_TYPE_QUANTITY]: DEFAULT_QUANTITY_RATE_UNIT_OPTIONS,
  };

  if (R.equals(rateType, GC.CHARGE_RATE_TYPE_STOP)) {
    return `${label} per ${G.getWindowLocale('titles:stop', 'Stop')}`;
  }

  if (R.equals(rateType, GC.CHARGE_RATE_TYPE_DISTANCE)) {
    const unit = R.propOr('', rateUnit, { [GC.UOM_MILE]: 'Mile', [GC.UOM_KILOMETER]: 'Km' });

    return unit ? `${label} per ${unit}` : label;
  }

  const unit = R.compose(
    R.propOr('', 'label'),
    R.find(R.propEq(rateUnit, 'value')),
  )(R.propOr([], rateType, unitOptions));

  if (R.not(unit)) return label;

  return R.equals(rateType, GC.CHARGE_RATE_TYPE_TIME) ? `${label} ${unit}` : `${label} per ${unit}`;
};

const getAccessorialLabel = (item) => {
  if (item.accessorialSource) return getConfiguredAccessorialLabel(item);

  const { advancePayment, displayedValue, internalExpense } = item;

  let label = displayedValue;

  if (G.isTrue(internalExpense)) {
    label = `${displayedValue} (${G.getWindowLocale('titles:internal-expense', 'Internal Expense')})`;
  }

  if (G.isTrue(advancePayment)) {
    label = `${displayedValue} (${G.getWindowLocale('titles:advance-payment', 'Advance Payment')})`;
  }

  return label;
};

export const getAccessorialOptionValue = (item) => {
  if (item.accessorialSource) return `${item.accessorialSource}:${item.guid}`;

  return item[GC.FIELD_ORIGINAL_CONFIG_GUID] || item[GC.FIELD_GUID] || item[GC.FIELD_DISPLAYED_VALUE];
};

const getOptionsFromAccessorials = (filter, accessorials, { grouped, includeContract }) => {
  const options = R.map((item) => ({
    accessorial: item,
    label: getAccessorialLabel(item),
    value: getAccessorialOptionValue(item),
    [GC.FIELD_ACCESSORIAL_CONFIG_GUID]: G.getPropFromObject(GC.FIELD_ORIGINAL_CONFIG_GUID, item),
    [GC.FIELD_SHARED_ASSESSORIAL_GUID]: G.getPropFromObject(GC.FIELD_SHARED_ASSESSORIAL_GUID, item),
  }), R.filter((item) => item.accessorialSource || filter(item), accessorials));

  if (R.not(grouped)) return R.prepend(GC.EMPTY_OPTION_OBJECT, options);

  const contractOptions = options.filter(({ accessorial }) => R.equals(accessorial.accessorialSource, 'contract'));
  const sharedOptions = options.filter(({ accessorial }) => R.equals(accessorial.accessorialSource, 'shared'));
  const generalOptions = options.filter(({ accessorial }) => G.isNilOrEmpty(accessorial.accessorialSource));

  return [
    ...(includeContract ? [{
      options: contractOptions,
      label: G.getWindowLocale('titles:contract-accessorials', 'Contract Accessorials'),
    }] : []),
    {
      options: sharedOptions,
      label: G.getWindowLocale('titles:shared-accessorials', 'Shared Accessorials'),
    },
    {
      options: R.prepend(GC.EMPTY_OPTION_OBJECT, generalOptions),
      label: G.getWindowLocale('titles:general-accessorials', 'General Accessorials'),
    },
  ];
};

export const getSelectedAccessorialOption = (options, value, charge = {}) => {
  const flatOptions = R.chain((option) => R.or(option.options, [option]), options);
  const configGuid = charge[GC.FIELD_ACCESSORIAL_CONFIG_GUID] || charge[GC.FIELD_ORIGINAL_CONFIG_GUID];

  const configuredOption = flatOptions.find(({ accessorial }) => {
    if (R.not(accessorial) || R.not(accessorial.accessorialSource)) return false;

    if (charge[GC.FIELD_SHARED_ASSESSORIAL_GUID]) {
      return accessorial[GC.FIELD_SHARED_ASSESSORIAL_GUID] === charge[GC.FIELD_SHARED_ASSESSORIAL_GUID];
    }

    return R.equals(accessorial.accessorialSource, 'contract')
      && (R.not(configGuid) || R.equals(accessorial[GC.FIELD_ORIGINAL_CONFIG_GUID], configGuid))
      && R.equals(accessorial[GC.FIELD_DISPLAYED_VALUE], value)
      && [GC.FIELD_RATE, GC.FIELD_CURRENCY, GC.FIELD_CHARGE_RATE_TYPE, GC.FIELD_CHARGE_RATE_UNIT]
        .every((field) => R.equals(accessorial[field], charge[field]));
  });

  const generalOption = flatOptions.find(({ accessorial }) => {
    if (R.not(accessorial) || accessorial.accessorialSource) return false;

    return configGuid
      ? R.equals(accessorial[GC.FIELD_ORIGINAL_CONFIG_GUID], configGuid)
      : R.equals(accessorial[GC.FIELD_DISPLAYED_VALUE], value);
  });

  return configuredOption || flatOptions.find((option) => option.value === value) || generalOption;
};

export const getAccessorialsForState = (data, loadType, {
  accessorialType,
  grouped = false,
  includeContract = true,
} = {}) => {
  const filteredData = accessorialType ? data.filter(({ types }) => (
    G.isNilOrEmpty(types) || types.includes(accessorialType)
  )) : data;

  const accessorials = R.compose(
    R.indexBy(getAccessorialOptionValue),
    R.map((item) => R.assoc(
      GC.FIELD_CHARGE_QUANTITY,
      R.and(R.equals(item.rateType, GC.CHARGE_RATE_TYPE_TIME), R.equals(item.rateUnit, GC.CHARGE_RATE_UNIT_TYPE_HOUR))
        ? R.propOr(null, GC.FIELD_CHARGE_QUANTITY, item) : 1,
      item,
    )),
  )(filteredData);

  const noFuelFilter = G.ifElse(
    G.isLoadTypeClo(loadType),
    noFuelAccessorialFilterForDO,
    noFuelAccessorialFilter,
  );

  const fuelFilter = G.ifElse(
    G.isLoadTypeClo(loadType),
    fuelAccessorialFilterForDO,
    fuelAccessorialFilter,
  );

  const groupOptions = { grouped, includeContract };
  const fuelSelectOptions = getOptionsFromAccessorials(fuelFilter, filteredData, groupOptions);
  const noFuelSelectOptions = getOptionsFromAccessorials(noFuelFilter, filteredData, groupOptions);

  return {
    accessorials,
    fuelSelectOptions,
    noFuelSelectOptions,
  };
};
// accessorials

// rate type/unit
const getDefaultRateUnit = (rateType, options) => {
  const map = {
    [GC.CHARGE_RATE_TYPE_TIME]: GC.CHARGE_RATE_UNIT_TYPE_HOUR,
    [GC.CHARGE_RATE_TYPE_DISCOUNT]: GC.CHARGE_RATE_UNIT_TYPE_FLAT,
    [GC.CHARGE_RATE_TYPE_LINE_HAUL]: GC.CHARGE_RATE_UNIT_TYPE_PERCENT,
    [GC.CHARGE_RATE_TYPE_CUSTOMER_RATE]: GC.CHARGE_RATE_UNIT_TYPE_FLAT,
    [GC.CHARGE_RATE_TYPE_TAXABLE_TOTAL]: GC.CHARGE_RATE_UNIT_TYPE_PERCENT,
    [GC.CHARGE_RATE_TYPE_CUSTOMER_LINE_HAUL]: GC.CHARGE_RATE_UNIT_TYPE_FLAT,
    [GC.CHARGE_RATE_TYPE_VOLUME]: G.ifElse(
      G.isNilOrEmpty(R.prop(GC.FIELD_TOTAL_TRIP_VOLUME, options)),
      '',
      R.prop(GC.FIELD_TOTAL_TRIP_VOLUME_UOM, options),
    ),
    [GC.CHARGE_RATE_TYPE_QUANTITY]: G.ifElse(
      G.isNilOrEmpty(R.prop(GC.FIELD_TOTAL_QUANTITIES, options)),
      '',
      R.head(R.keys(R.pathOr({}, [GC.FIELD_TOTAL_QUANTITIES], options))),
    ),
    [GC.CHARGE_RATE_TYPE_DISTANCE]: G.ifElse(
      G.isNilOrEmpty(R.prop(GC.FIELD_TOTAL_TRIP_DISTANCE_UOM, options)),
      GC.UOM_KILOMETER,
      R.prop(GC.FIELD_TOTAL_TRIP_DISTANCE_UOM, options),
    ),
    [GC.CHARGE_RATE_TYPE_DEADHEAD_DISTANCE]: G.ifElse(
      G.isNilOrEmpty(R.prop(GC.FIELD_DEADHEAD_DISTANCE_UOM, options)),
      GC.UOM_KILOMETER,
      R.prop(GC.FIELD_DEADHEAD_DISTANCE_UOM, options),
    ),
    [GC.CHARGE_RATE_TYPE_WEIGHT]: G.ifElse(
      G.isNilOrEmpty(R.prop(GC.FIELD_TOTAL_TRIP_WEIGHT_UOM, options)),
      GC.UOM_KILOGRAM,
      R.prop(GC.FIELD_TOTAL_TRIP_WEIGHT_UOM, options),
    ),
  };

  return R.prop(rateType, map);
};

export const getDefaultChargeUnitFromChargeRateType = (rateType, props) => {
  const pick = [
    GC.FIELD_TOTAL_QUANTITIES,
    GC.FIELD_TOTAL_TRIP_VOLUME,
    GC.FIELD_DEADHEAD_DISTANCE,
    GC.FIELD_TOTAL_TRIP_WEIGHT,
    GC.FIELD_TOTAL_TRIP_DISTANCE,
    GC.FIELD_TOTAL_TRIP_VOLUME_UOM,
    GC.FIELD_DEADHEAD_DISTANCE_UOM,
    GC.FIELD_TOTAL_TRIP_WEIGHT_UOM,
    GC.FIELD_TOTAL_TRIP_DISTANCE_UOM,
  ];

  const options = R.pick(pick, props);

  return getDefaultRateUnit(rateType, options);
};

// calc charge total
export const isCustomerRateWithPercent = (rateType, rateUnit) => G.isAllTrue(
  R.equals(rateType, GC.CHARGE_RATE_TYPE_CUSTOMER_RATE),
  R.equals(rateUnit, GC.CHARGE_RATE_UNIT_TYPE_PERCENT),
);

export const isCustomerLineHaulWithPercent = (rateType, rateUnit) => G.isAllTrue(
  R.equals(rateType, GC.CHARGE_RATE_TYPE_CUSTOMER_LINE_HAUL),
  R.equals(rateUnit, GC.CHARGE_RATE_UNIT_TYPE_PERCENT),
);

export const isDiscountWithPercent = (rateType, rateUnit) => G.isAllTrue(
  R.equals(rateType, GC.CHARGE_RATE_TYPE_DISCOUNT),
  R.equals(rateUnit, GC.CHARGE_RATE_UNIT_TYPE_PERCENT),
);

export const isLineHaulWithPercent = (rateType, rateUnit) => G.isAllTrue(
  R.equals(rateType, GC.CHARGE_RATE_TYPE_LINE_HAUL),
  R.equals(rateUnit, GC.CHARGE_RATE_UNIT_TYPE_PERCENT),
);

export const isTaxableTotalWithPercent = (rateType, rateUnit) => G.isAllTrue(
  R.equals(rateType, GC.CHARGE_RATE_TYPE_TAXABLE_TOTAL),
  R.equals(rateUnit, GC.CHARGE_RATE_UNIT_TYPE_PERCENT),
);

export const calcSimpleChargeTotal = (chargeState) => R.multiply(
  R.prop(GC.FIELD_CHARGE_RATE, chargeState),
  R.propOr(1, GC.FIELD_CHARGE_QUANTITY, chargeState),
);
